import AiAnalysisReference from "./AiAnalysisReference";
import { v4 as uuidv4 } from 'uuid';
import AiExplanation from './AiExplanation';
import AiDisclaimer from './AiDisclaimer';
import { AI_TEXT } from './ContextAiCard';
import { aiQuotaErrorMessage } from './aiQuotaErrors';
import { aiEvents } from '../services/doctorServices/ContextAiService';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import { useTranslation } from "react-i18next";
import { PANEL } from "../design-system/panel/panelTokens";
import {
  panelAssistantFabSx,
  panelAssistantShellSx,
  panelAssistantAccentBarSx,
  panelAssistantHeaderSx,
  panelAssistantHeaderIconWrapSx,
  panelAssistantHeaderTitleSx,
  panelAssistantHeaderSubtitleSx,
  panelAssistantStatusDotSx,
  panelAssistantHeaderButtonSx,
  panelAssistantBodySx,
  panelAssistantHintCardSx,
  panelAssistantHintIconWrapSx,
  panelAssistantHintTitleSx,
  panelAssistantHintBodySx,
  panelAssistantPrivacySx,
  panelAssistantUserBubbleSx,
  panelAssistantAssistBubbleSx,
  panelAssistantThinkingSx,
  panelAssistantThinkingTextSx,
  panelAssistantErrorSx,
  panelAssistantComposerWrapSx,
  panelAssistantComposerSx,
  panelAssistantInputSx,
  panelAssistantSendButtonSx,
} from "../design-system/panel/panelAssistantSx";
import { useLocation } from "react-router-dom";
import AiChatService from "../services/doctorServices/AiChatService";
import ChatMarkdown from "./ChatMarkdown";
import PanelTextField from "./panel/PanelTextField";

function resolveLocale(lang) {
  const base = String(lang || "tr").split("-")[0].toLowerCase();
  if (base === "en" || base === "fr") return base;
  return "tr";
}

/**
 * Global floating assistant — read-only tool-backed Q&A for doctors.
 */
export default function AiAssistantWidget() {
  const location = useLocation();
  const isMediaVision = location.pathname.includes("media-vision");
  const isPatientExtraction = location.pathname.includes("patient-extraction");
  const { t, i18n } = useTranslation("common");
  const service = useMemo(() => new AiChatService(), []);
  const locale = resolveLocale(i18n.resolvedLanguage || i18n.language);

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analysisSources, setAnalysisSources] = useState([]);
  const [analysisId, setAnalysisId] = useState(null);
  const [analysisReference, setAnalysisReference] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);
  const [turnStatus, setTurnStatus] = useState(null);
  const [activeTurnId, setActiveTurnId] = useState(null);
  const [restoreVersion, setRestoreVersion] = useState(0);
  const [error, setError] = useState(null);
  const [hasActiveOverlay, setHasActiveOverlay] = useState(false);
  const bottomRef = useRef(null);
  const conversationVersion = useRef(0);
  useEffect(() => {
    const openContext = event => {
      if (!event.detail?.analysisId) return;
      conversationVersion.current += 1; setSending(false); setDraft('');
      setRestoreVersion(value => value + 1);
      setAnalysisReference({ kind: event.detail.kind, generatedAt: event.detail.generatedAt });
      setAnalysisSources(event.detail.sources || []); setAnalysisId(event.detail.analysisId); setSessionId(null); setMessages([]); setError(null); setOpen(true);
    };
    window.addEventListener(aiEvents.open, openContext);
    return () => window.removeEventListener(aiEvents.open, openContext);
  }, []);
  useEffect(() => {
    conversationVersion.current += 1; setSending(false);
    setAnalysisReference(null); setAnalysisSources([]); setAnalysisId(null); setSessionId(null); setMessages([]); setDraft('');
  }, [location.pathname, location.search]);


  const applySession = useCallback(data => {
    if (!data?.session) return;
    setSessionId(data.session.id);
    if (data.session.analysis_id) setAnalysisId(data.session.analysis_id);
    if (data.analysis) setAnalysisReference({ kind: data.analysis.kind, generatedAt: data.analysis.updated_at || data.analysis.created_at });
    setMessages(data.messages || []);
    setAnalysisSources(data.sources || []);
    const status = data.turn?.status;
    setTurnStatus(status || null);
    setActiveTurnId(['queued', 'processing'].includes(status) ? data.turn.id : null);
    setSending(status === 'queued' || status === 'processing');
    if (status === 'failed') {
      const code = data.turn?.error_code;
      setError(aiQuotaErrorMessage(code, locale) || (code === 'AI_WORKER_INTERRUPTED'
          ? { tr: 'İşlem kesildi. Yeniden gönderebilirsiniz.', en: 'Processing was interrupted. You can send again.', fr: 'Traitement interrompu. Vous pouvez réessayer.' }[locale]
          : t('aiAssistantError')));
    }
  }, [locale, t]);

  // Resume the server-owned conversation after navigation or a full page reload.
  useEffect(() => {
    let active = true;
    const version = ++conversationVersion.current;
    setLoadingSession(true);
    service.getLatestSession(analysisId).then(data => {
      if (active && conversationVersion.current === version) applySession(data);
    }).catch(() => {
      if (active && conversationVersion.current === version) setError(t('aiAssistantError'));
    }).finally(() => {
      if (active && conversationVersion.current === version) setLoadingSession(false);
    });
    return () => { active = false; };
  }, [analysisId, location.pathname, location.search, restoreVersion, service, applySession, t]);

  useEffect(() => {
    if (!sessionId || !sending || !activeTurnId) return undefined;
    let active = true;
    let timer;
    const version = conversationVersion.current;
    const poll = async () => {
      try {
        const data = await service.getSession(sessionId);
        if (!active || conversationVersion.current !== version) return;
        applySession(data);
        if (['queued', 'processing'].includes(data?.turn?.status)) timer = setTimeout(poll, 2000);
      } catch (e) {
        if (!active || conversationVersion.current !== version) return;
        if ([400, 401, 403, 404, 409].includes(e?.response?.status)) {
          setSending(false); setMessages([]); setError(t('aiAssistantError'));
        } else timer = setTimeout(poll, 4000);
      }
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [sessionId, sending, activeTurnId, service, applySession, t]);


  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, sending]);

  // Specifically for patient-extraction: hide FAB when an active modal/menu is open
  useEffect(() => {
    if (!isPatientExtraction) return undefined;
    const checkOverlay = () => {
      const hasOverlay = Boolean(document.querySelector(".MuiDialog-root, .MuiMenu-root"));
      setHasActiveOverlay(hasOverlay);
    };
    const interval = setInterval(checkOverlay, 300);
    return () => clearInterval(interval);
  }, [isPatientExtraction]);

  if (isMediaVision) {
    return null;
  }

  // Hide FAB in patient extraction if user opened a dialog or overflow menu
  if (isPatientExtraction && hasActiveOverlay && !open) {
    return null;
  }

  const send = async () => {
    const requested = conversationVersion.current;
    const text = draft.trim();
    if (!text || sending || loadingSession) return;
    setDraft("");
    setError(null);
    setMessages((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: "user", content: text },
    ]);
    setSending(true);
    setTurnStatus('queued');
    setActiveTurnId(null);
    try {
      const data = await service.sendMessage({
        message: text,
        requestId: uuidv4(),
        sessionId,
        analysisId,
        locale,
      });
      if (conversationVersion.current !== requested) return;
      if (data?.session_id) setSessionId(data.session_id);
      setTurnStatus(data?.turn?.status || null);
      setActiveTurnId(['queued', 'processing'].includes(data?.turn?.status) ? data.turn.id : null);
      setSending(['queued', 'processing'].includes(data?.turn?.status));
      if (data?.message) {
        setMessages((prev) => [...prev, data.message]);
      }
    } catch (e) {
      if (conversationVersion.current !== requested) return;
      setSending(false);
      // The request may have committed before the connection dropped.
      if (!e?.response || e?.response?.status === 409) {
        try {
          const recovered = await service.getLatestSession(analysisId);
          if (conversationVersion.current !== requested) return;
          if (['queued', 'processing'].includes(recovered?.turn?.status)) { applySession(recovered); return; }
        } catch { /* Keep the original error; never resubmit a possibly billed turn. */ }
      }
      const respData = e?.response?.data;
      const code = respData?.code || respData?.error;
      const message = respData?.message || respData?.error;

      const quotaMessage = aiQuotaErrorMessage(code, locale);
      if (quotaMessage) {
        setError(quotaMessage);
      } else if (e?.response?.status === 403) {
        setError(message || { tr: 'Bu işlem için erişim izniniz bulunmuyor.', en: 'You do not have access to this action.', fr: 'Vous n’avez pas accès à cette action.' }[locale]);
      } else {
        setError(
          code === "llm_unconfigured"
            ? t("aiAssistantUnavailable")
            : t("aiAssistantError")
        );
      }
    }
  };

  const hintCards = [
    {
      key: "usage",
      icon: <MenuBookOutlinedIcon sx={{ fontSize: 18 }} />,
      title: t("aiAssistantHintUsageTitle"),
      body: t("aiAssistantHintUsageBody"),
      tone: "accent",
    },
    {
      key: "data",
      icon: <QueryStatsOutlinedIcon sx={{ fontSize: 18 }} />,
      title: t("aiAssistantHintDataTitle"),
      body: t("aiAssistantHintDataBody"),
      tone: "blue",
    },
  ];

  return createPortal(
    <>
      <Fab
        aria-label={t("aiAssistantTitle")}
        onClick={() => setOpen((v) => !v)}
        sx={{
          ...panelAssistantFabSx(open),
          width: { xs: 38, sm: 40 },
          height: { xs: 38, sm: 40 },
          minHeight: "unset",
          right: { xs: 14, sm: 18 },
          bottom: { xs: 26, sm: 30 },
          opacity: open ? 1 : 0.85,
          "&:hover": {
            opacity: 1,
            transform: "translateY(-1px) scale(1.04)",
          },
          ...(isPatientExtraction
            ? {
                width: 38,
                height: 38,
                right: { xs: 12, sm: 16 },
                bottom: { xs: 12, sm: 16 },
                opacity: open ? 1 : 0.85,
              }
            : {}),
        }}
      >
        {open ? (
          <CloseOutlinedIcon sx={{ fontSize: 18 }} />
        ) : (
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
        )}
      </Fab>

      {open ? (
        <Box sx={panelAssistantShellSx(expanded)}>
          <Box sx={panelAssistantAccentBarSx} />

          <Box sx={panelAssistantHeaderSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.15, minWidth: 0, flex: 1 }}>
              <Box sx={panelAssistantHeaderIconWrapSx}>
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={panelAssistantHeaderTitleSx}>{t("aiAssistantTitle")}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: 0.25 }}>
                  <Box sx={panelAssistantStatusDotSx} />
                  <Typography sx={panelAssistantHeaderSubtitleSx}>{analysisId ? AI_TEXT[locale].context : t("aiAssistantSubtitle")}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexShrink: 0 }}>
              <IconButton
                type="button"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExpanded((v) => !v);
                }}
                aria-label={expanded ? t("aiAssistantCollapse") : t("aiAssistantExpand")}
                sx={panelAssistantHeaderButtonSx}
              >
                {expanded ? (
                  <CloseFullscreenOutlinedIcon sx={{ fontSize: 15 }} />
                ) : (
                  <OpenInFullOutlinedIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
              <IconButton
                type="button"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(false);
                  setExpanded(false);
                }}
                aria-label={t("aiAssistantClose")}
                sx={panelAssistantHeaderButtonSx}
              >
                <CloseOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          <AiAnalysisReference analysisId={analysisId} kind={analysisReference?.kind} generatedAt={analysisReference?.generatedAt} sources={analysisSources} locale={locale} />
          <Box sx={panelAssistantBodySx}>
            <AiDisclaimer locale={locale} />
            {messages.length === 0 && !analysisId ? (
              <Box sx={{ mt: 0.25, display: "flex", flexDirection: "column", gap: 1.15 }}>
                {hintCards.map((card) => (
                  <Box key={card.key} sx={panelAssistantHintCardSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, mb: 0.55 }}>
                      <Box sx={panelAssistantHintIconWrapSx(card.tone)}>{card.icon}</Box>
                      <Typography sx={panelAssistantHintTitleSx}>{card.title}</Typography>
                    </Box>
                    <Typography sx={panelAssistantHintBodySx}>{card.body}</Typography>
                  </Box>
                ))}
                <Typography sx={panelAssistantPrivacySx}>{t("aiAssistantHintPrivacy")}</Typography>
              </Box>
            ) : null}

            {messages.map((m) => {
              const mine = m.role === "user";
              return mine ? (
                <Box key={m.id} sx={panelAssistantUserBubbleSx}>
                  {m.content}
                </Box>
              ) : (
                <Box key={m.id} sx={panelAssistantAssistBubbleSx}>
                  <>{analysisId ? <AiExplanation text={m.content} sources={analysisSources} locale={locale} /> : <ChatMarkdown content={m.content} variant="panel" />}</>
                </Box>
              );
            })}

            {sending ? (
              <Box sx={panelAssistantThinkingSx}>
                <CircularProgress size={13} sx={{ color: PANEL.accent.main }} />
                <Typography sx={panelAssistantThinkingTextSx}>{turnStatus === 'queued' ? { tr: 'Sırada. Sayfadan ayrılsanız da devam eder.', en: 'Queued. Processing continues if you leave.', fr: 'En attente. Le traitement continue si vous quittez la page.' }[locale] : t("aiAssistantThinking")}</Typography>
              </Box>
            ) : null}

            {error ? <Typography sx={panelAssistantErrorSx}>{error}</Typography> : null}
            <div ref={bottomRef} />
          </Box>

          <Box sx={panelAssistantComposerWrapSx}>
            <Box sx={panelAssistantComposerSx}>
              <PanelTextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder={t("aiAssistantPlaceholder")}
                value={draft}
                disabled={sending || loadingSession}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                sx={panelAssistantInputSx}
              />
              <IconButton
                onClick={send}
                disabled={sending || loadingSession || !draft.trim()}
                sx={panelAssistantSendButtonSx}
              >
                <SendRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      ) : null}
    </>,
    document.body
  );
}
